import React, { useState, useMemo } from "react";
import { StyleSheet, View, ScrollView, TouchableOpacity, TextInput } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { router } from "expo-router";
import ThemedScreen from "@/components/ThemedScreen";
import { ThemedText } from "@/components/ThemedText";
import { ThemedPanel } from "@/components/ThemedPanel";
import { ThemedButton } from "@/components/ThemedButton";
import { DateSelector } from "@/components/date/DateSelector";
import { useThemeColor } from "@/hooks/useThemeColor";
import { DateParser, IDate, IDatePermutation } from "common247";

/**
 * Interactive Design Tester for DateParser (Feature 356).
 * Enables fine-tuning, real-time interactive parsing, bidirectional DatePicker integration,
 * and comprehensive verification of all supported syntax permutations.
 */
export default function DateParserDesignScreen() {
  const tintColor = useThemeColor({}, "tint");
  const cardBg = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const textMuted = useThemeColor({}, "textMuted");
  const badgeBg = useThemeColor({}, "buttonSecondary");
  const readOnlyBg = useThemeColor({}, "inputReadOnly");
  const successColor = "#2e7d32";

  // Interactive sandbox state
  const [inputText, setInputText] = useState("Oct-26");
  const [activeJsonPayload, setActiveJsonPayload] = useState<IDate>(() =>
    DateParser.parse("Oct-26")
  );
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const [datePickerVisible, setDatePickerVisible] = useState(false);
  const [dateAnchor, setDateAnchor] = useState<{ x: number; y: number } | null>(null);

  // Supported permutations from DateParser
  const permutations = useMemo(() => DateParser.getSupportedPermutations(), []);

  const categories = useMemo(() => {
    const set = new Set(permutations.map((p) => p.category));
    return ["All", ...Array.from(set)];
  }, [permutations]);

  const filteredPermutations = useMemo(() => {
    if (selectedCategory === "All") return permutations;
    return permutations.filter((p) => p.category === selectedCategory);
  }, [permutations, selectedCategory]);

  // Live parsed result from currently typed text
  const liveParsed = useMemo(() => {
    return DateParser.parse(inputText);
  }, [inputText]);

  // Handler when user leaves input field (onBlur)
  const handleInputBlur = () => {
    const parsed = DateParser.parse(inputText);
    setInputText(parsed.formatted);
    setActiveJsonPayload(parsed);
  };

  // Handler to load any permutation into the live sandbox
  const handleLoadPermutation = (perm: IDatePermutation) => {
    setInputText(perm.example);
    const parsed = DateParser.parse(perm.example);
    setActiveJsonPayload(parsed);
  };

  // Stringified JSON view of active payload
  const jsonOutputString = useMemo(() => {
    return JSON.stringify(activeJsonPayload, null, 2);
  }, [activeJsonPayload]);

  return (
    <ThemedScreen
      title="Design: Date Parser Tester"
      leftIcon={{
        name: "arrow-back",
        onPress: () => router.navigate("/admin/design" as any),
      }}
    >
      <ScrollView contentContainerStyle={styles.container}>
        {/* Banner Header */}
        <ThemedPanel padding={14} elevation="low">
          <View style={styles.bannerHeader}>
            <View style={[styles.iconBox, { backgroundColor: badgeBg }]}>
              <Ionicons name="sparkles" size={22} color={tintColor} />
            </View>
            <View style={{ flex: 1 }}>
              <ThemedText type="defaultSemiBold" style={styles.bannerTitle}>
                DateParser & Range Formatter Tester
              </ThemedText>
              <ThemedText style={[styles.bannerSub, { color: textMuted }]}>
                Design Sign-off Workbench (Feature 356)
              </ThemedText>
            </View>
          </View>
          <ThemedText style={styles.bannerText}>
            Converts free-text date inputs into standard JSON date ranges matching DatePicker.
            Automatically fills missing years with the current year (2026), expands 2-digit years,
            detects approximate "~" markers, splits "-" and "to" ranges, extracts durations (minutes, hours,
            days, weeks, months, years), and formats dates upon field blur.
          </ThemedText>
        </ThemedPanel>

        {/* SECTION 1: LIVE INTERACTIVE SANDBOX */}
        <View style={styles.sectionHeaderRow}>
          <ThemedText type="stamp" style={{ color: textMuted }}>
            LIVE INTERACTIVE SANDBOX
          </ThemedText>
        </View>

        <ThemedPanel padding={14} elevation="low" style={styles.sandboxCard}>
          <ThemedText type="defaultSemiBold" style={{ fontSize: 15, marginBottom: 4 }}>
            Input Date Text Field
          </ThemedText>
          <ThemedText style={[styles.inputHint, { color: textMuted }]}>
            Type any date or duration expression (e.g. "Oct-26", "10-Oct-2026 for 2 hours", "10-Oct-2026 - 15-Oct-2026", "09-Oct + 3 days").
            Leaving the field (`onBlur`) formats and normalizes the text.
          </ThemedText>

          <View style={styles.inputRow}>
            <TextInput
              testID="date-parser-input"
              value={inputText}
              onChangeText={(text) => {
                setInputText(text);
                setActiveJsonPayload(DateParser.parse(text));
              }}
              onBlur={handleInputBlur}
              placeholder="e.g. 10-Oct-2026 for 2 hours, 10-Oct - 15-Oct"
              placeholderTextColor="#888"
              style={[
                styles.textInput,
                {
                  backgroundColor: cardBg,
                  borderColor: borderColor,
                  color: "#222",
                },
              ]}
              autoCapitalize="none"
              autoCorrect={false}
            />
            <ThemedButton
              title="Format (Blur)"
              variant="outline"
              size="compact"
              onPress={handleInputBlur}
              style={{ marginLeft: 8 }}
            />
          </View>

          {/* Quick Example Chips for Durations and Range Separators */}
          <View style={styles.presetChipsRow}>
            {[
              "10-Oct-2026 for 2 hours",
              "10-Oct-2026 - 15-Oct-2026",
              "09-Oct-2026 14:00 + 30 mins",
              "10-Oct-2026 + 3 days",
              "Oct 2026 for 2 months",
              "10-Oct-2026 (1 week)",
            ].map((eg) => (
              <TouchableOpacity
                key={eg}
                style={[styles.presetChip, { borderColor, backgroundColor: badgeBg }]}
                onPress={() => {
                  setInputText(eg);
                  const p = DateParser.parse(eg);
                  setActiveJsonPayload(p);
                }}
              >
                <ThemedText style={[styles.presetChipText, { color: tintColor }]}>
                  {eg}
                </ThemedText>
              </TouchableOpacity>
            ))}
          </View>

          {/* Quick Metrics & Parsed Summary */}
          <View style={styles.metricRow}>
            <View style={[styles.badgePill, { backgroundColor: badgeBg }]}>
              <ThemedText style={[styles.badgeLabel, { color: textMuted }]}>Type: </ThemedText>
              <ThemedText type="defaultSemiBold" style={{ color: tintColor, fontSize: 12 }}>
                {liveParsed.type.toUpperCase()}
              </ThemedText>
            </View>

            <View style={[styles.badgePill, { backgroundColor: badgeBg }]}>
              <ThemedText style={[styles.badgeLabel, { color: textMuted }]}>Precision: </ThemedText>
              <ThemedText type="defaultSemiBold" style={{ color: tintColor, fontSize: 12 }}>
                {liveParsed.from?.precision || "unknown"}
              </ThemedText>
            </View>

            {liveParsed.duration && (
              <View style={[styles.badgePill, { backgroundColor: "#e3f2fd", borderColor: "#90caf9" }]}>
                <Ionicons name="time-outline" size={13} color="#1565c0" style={{ marginRight: 3 }} />
                <ThemedText type="defaultSemiBold" style={{ color: "#0d47a1", fontSize: 12 }}>
                  Duration: {liveParsed.duration}
                </ThemedText>
              </View>
            )}

            {liveParsed.from?.isApproximate && (
              <View style={[styles.badgePill, { backgroundColor: "#fff3e0", borderColor: "#ffe082" }]}>
                <Ionicons name="help-circle-outline" size={13} color="#f57c00" style={{ marginRight: 3 }} />
                <ThemedText type="defaultSemiBold" style={{ color: "#e65100", fontSize: 12 }}>
                  Approximate (~)
                </ThemedText>
              </View>
            )}

            <View style={[styles.badgePill, { backgroundColor: "#e8f5e9" }]}>
              <Ionicons name="checkmark-circle-outline" size={13} color={successColor} style={{ marginRight: 3 }} />
              <ThemedText type="defaultSemiBold" style={{ color: successColor, fontSize: 12 }}>
                {liveParsed.formatted}
              </ThemedText>
            </View>
          </View>

          {/* DatePicker Handshake Action */}
          <View style={styles.pickerActionRow}>
            <ThemedButton
              title="Open in DatePicker Popover"
              variant="secondary"
              icon={<Ionicons name="calendar-outline" size={16} color={tintColor} style={{ marginRight: 6 }} />}
              onPress={(e: any) => {
                const x = e?.nativeEvent?.pageX ?? e?.nativeEvent?.clientX ?? 0;
                const y = e?.nativeEvent?.pageY ?? e?.nativeEvent?.clientY ?? 0;
                setDateAnchor(x || y ? { x, y } : null);
                setDatePickerVisible(true);
              }}
            />
            <ThemedText style={[styles.handshakeHelp, { color: textMuted }]}>
              Tests bidirectional payload exchange with DatePicker
            </ThemedText>
          </View>

          {/* Stored JSON View */}
          <View style={styles.jsonBlock}>
            <View style={styles.jsonHeader}>
              <ThemedText type="defaultSemiBold" style={{ fontSize: 12, color: textMuted }}>
                Stored JSON Date Definition (IDate)
              </ThemedText>
              <View style={[styles.jsonStoredTag, { backgroundColor: badgeBg }]}>
                <ThemedText style={{ fontSize: 10, color: tintColor }}>Canonical Schema</ThemedText>
              </View>
            </View>
            <View style={[styles.codeContainer, { backgroundColor: readOnlyBg, borderColor }]}>
              <ThemedText style={styles.codeText}>{jsonOutputString}</ThemedText>
            </View>
          </View>
        </ThemedPanel>

        {/* SECTION 2: SUPPORTED PERMUTATIONS CATALOG */}
        <View style={styles.sectionHeaderRow}>
          <ThemedText type="stamp" style={{ color: textMuted }}>
            SUPPORTED PERMUTATIONS CATALOG ({filteredPermutations.length})
          </ThemedText>
        </View>

        {/* Category Filters */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.categoryPillsRow}>
          {categories.map((cat) => (
            <TouchableOpacity
              key={cat}
              onPress={() => setSelectedCategory(cat)}
              style={[
                styles.catPill,
                {
                  backgroundColor: selectedCategory === cat ? tintColor : badgeBg,
                  borderColor: selectedCategory === cat ? tintColor : borderColor,
                },
              ]}
              activeOpacity={0.7}
            >
              <ThemedText
                type="defaultSemiBold"
                style={{
                  color: selectedCategory === cat ? "#fff" : textMuted,
                  fontSize: 12,
                }}
              >
                {cat}
              </ThemedText>
            </TouchableOpacity>
          ))}
        </ScrollView>

        {/* Permutation Cards Grid */}
        <View style={styles.permGrid}>
          {filteredPermutations.map((perm) => (
            <ThemedPanel key={perm.id} padding={12} elevation="low" style={styles.permCard}>
              <View style={styles.permHeader}>
                <View style={styles.permTitleRow}>
                  <ThemedText type="defaultSemiBold" style={styles.permSyntax}>
                    {perm.syntax}
                  </ThemedText>
                  <View style={[styles.permTypeBadge, { backgroundColor: badgeBg }]}>
                    <ThemedText style={{ fontSize: 10, color: tintColor }}>
                      {perm.expectedType}
                    </ThemedText>
                  </View>
                </View>

                <TouchableOpacity
                  testID={`btn-load-${perm.id}`}
                  style={[styles.testInSandboxBtn, { backgroundColor: badgeBg }]}
                  onPress={() => handleLoadPermutation(perm)}
                  activeOpacity={0.7}
                >
                  <Ionicons name="arrow-up-circle-outline" size={14} color={tintColor} style={{ marginRight: 4 }} />
                  <ThemedText style={[styles.testInSandboxText, { color: tintColor }]}>
                    Test in Sandbox
                  </ThemedText>
                </TouchableOpacity>
              </View>

              <ThemedText style={[styles.permDesc, { color: textMuted }]}>
                {perm.description}
              </ThemedText>

              <View style={styles.permComparisonRow}>
                <View style={styles.comparisonBox}>
                  <ThemedText style={[styles.compLabel, { color: textMuted }]}>Input:</ThemedText>
                  <View style={[styles.chip, { backgroundColor: readOnlyBg, borderColor }]}>
                    <ThemedText style={styles.chipText}>{perm.example}</ThemedText>
                  </View>
                </View>

                <Ionicons name="arrow-forward" size={14} color={textMuted} style={{ alignSelf: "center", marginHorizontal: 6 }} />

                <View style={styles.comparisonBox}>
                  <ThemedText style={[styles.compLabel, { color: textMuted }]}>Formatted Result:</ThemedText>
                  <View style={[styles.chip, { backgroundColor: "#e8f5e9", borderColor: "#c8e6c9" }]}>
                    <ThemedText style={[styles.chipText, { color: successColor }]}>
                      {perm.expectedFormatted}
                    </ThemedText>
                  </View>
                </View>
              </View>
            </ThemedPanel>
          ))}
        </View>

        {/* Contextual DateSelector Popover for Interactive Testing */}
        <DateSelector
          visible={datePickerVisible}
          onClose={() => setDatePickerVisible(false)}
          value={activeJsonPayload}
          type={activeJsonPayload.type === "range" ? "Range" : "Date"}
          anchorPosition={dateAnchor}
          onApply={(payload) => {
            setActiveJsonPayload(payload);
            setInputText(payload.formatted);
            setDatePickerVisible(false);
          }}
        />
      </ScrollView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 16,
    paddingBottom: 48,
  },
  bannerHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  iconBox: {
    width: 40,
    height: 40,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  bannerTitle: {
    fontSize: 16,
  },
  bannerSub: {
    fontSize: 12,
    marginTop: 2,
  },
  bannerText: {
    fontSize: 13,
    lineHeight: 18,
    marginTop: 4,
  },
  sectionHeaderRow: {
    marginTop: 20,
    marginBottom: 8,
  },
  sandboxCard: {
    marginBottom: 16,
  },
  inputHint: {
    fontSize: 12,
    marginBottom: 10,
    lineHeight: 16,
  },
  inputRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 12,
  },
  textInput: {
    flex: 1,
    height: 40,
    borderWidth: 1,
    borderRadius: 6,
    paddingHorizontal: 12,
    fontSize: 14,
  },
  presetChipsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
    marginBottom: 12,
  },
  presetChip: {
    borderWidth: 1,
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  presetChipText: {
    fontSize: 11,
    fontWeight: "600",
  },
  metricRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 14,
  },
  badgePill: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.06)",
  },
  badgeLabel: {
    fontSize: 11,
  },
  pickerActionRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 14,
    flexWrap: "wrap",
    gap: 12,
  },
  handshakeHelp: {
    fontSize: 12,
  },
  jsonBlock: {
    marginTop: 8,
  },
  jsonHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
  },
  jsonStoredTag: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 3,
  },
  codeContainer: {
    borderWidth: 1,
    borderRadius: 6,
    padding: 10,
  },
  codeText: {
    fontFamily: "monospace",
    fontSize: 11,
    lineHeight: 16,
  },
  categoryPillsRow: {
    flexDirection: "row",
    gap: 6,
    marginBottom: 12,
  },
  catPill: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 14,
    borderWidth: 1,
  },
  permGrid: {
    gap: 10,
  },
  permCard: {
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.06)",
  },
  permHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
  },
  permTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  permSyntax: {
    fontSize: 13,
  },
  permTypeBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 3,
  },
  testInSandboxBtn: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  testInSandboxText: {
    fontSize: 11,
    fontWeight: "600",
  },
  permDesc: {
    fontSize: 12,
    lineHeight: 16,
    marginBottom: 8,
  },
  permComparisonRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  comparisonBox: {
    flex: 1,
  },
  compLabel: {
    fontSize: 10,
    marginBottom: 2,
  },
  chip: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    borderWidth: 1,
  },
  chipText: {
    fontSize: 12,
    fontWeight: "500",
  },
});
